import { useState, useEffect } from 'react';
import { apiClient } from '../services/apiClient';

export default function Users() {
  const [tenantId, setTenantId] = useState<string>('');
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [fetchingAvailable, setFetchingAvailable] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ total: 0, synced: 0 });

  // Get current user's tenant on mount
  useEffect(() => {
    apiClient.get('/api/auth/me')
      .then((data) => {
        if (data.success) {
          setTenantId(data.data.tenantId);
        }
      })
      .catch((error) => console.error('Failed to get user info:', error));
  }, []);

  // Load users when tenant or page changes
  useEffect(() => {
    if (tenantId) {
      loadUsers();
    }
  }, [tenantId, page]);
  const loadUsers = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const data = await apiClient.get(`/api/users/${tenantId}?page=${page}&limit=50`);
      if (data.success) {
        setUsers(data.data);
        setTotalPages(data.pagination.totalPages);
        setStats({
          total: data.pagination.total,
          synced: data.data.length,
        });
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    }
    setLoading(false);
  };

  const handleSync = async () => {
    if (!tenantId) return;

    setSyncing(true);
    try {
      const data = await apiClient.post(`/api/users/sync/${tenantId}`);

      if (data.success) {
        alert(
          `Sync complete!\n\nSynced: ${data.data.synced}\nCreated: ${data.data.created}\nUpdated: ${data.data.updated}\n${
            data.data.errors.length > 0
              ? `\nErrors: ${data.data.errors.length}`
              : ''
          }`
        );
        loadUsers();
      } else {
        // Check if error requires admin consent
        if (data.error && (data.error.includes('Insufficient privileges') || data.error.includes('missing service principal') || data.error.includes('AADSTS7000229'))) {
          if (confirm(
            'Admin consent is required to sync users from Microsoft 365.\n\n' +
            'This requires granting VirtueSign permission to read users and groups from your organization.\n\n' +
            'Click OK to grant admin consent now.'
          )) {
            handleAdminConsent();
          }
        } else {
          alert(`Sync failed: ${data.error}`);
        }
      }
    } catch (error: any) {
      // Check if error requires admin consent
      if (error.message && (error.message.includes('Insufficient privileges') || error.message.includes('missing service principal') || error.message.includes('AADSTS7000229'))) {
        if (confirm(
          'Admin consent is required to sync users from Microsoft 365.\n\n' +
          'This requires granting VirtueSign permission to read users and groups from your organization.\n\n' +
          'Click OK to grant admin consent now.'
        )) {
          handleAdminConsent();
        }
      } else {
        alert(`Sync failed: ${error.message}`);
      }
    }
    setSyncing(false);
  };

  const handleAdminConsent = async () => {
    try {
      const data = await apiClient.get(`/api/auth/admin-consent-url/${tenantId}`);
      if (data.success) {
        // Redirect to Microsoft admin consent page
        window.location.href = data.data.consentUrl;
      } else {
        alert(`Failed to generate consent URL: ${data.error}`);
      }
    } catch (error: any) {
      alert(`Failed to generate consent URL: ${error.message}`);
    }
  };

  // Client-side filtering
  const filteredUsers = searchQuery
    ? users.filter(
        (user) =>
          user.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.jobTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.department?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : users;

  return (
    <div>
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ margin: '0 0 10px' }}>Users</h2>
        <p style={{ color: '#666', margin: 0 }}>
          Manage synced users from Microsoft 365
        </p>
      </div>

      {/* Info Banner */}
      <div
        style={{
          background: '#dbeafe',
          padding: '15px 20px',
          borderRadius: '8px',
          marginBottom: '25px',
          fontSize: '14px',
          color: '#1e40af',
          borderLeft: '4px solid #3b82f6',
        }}
      >
        💡 Users are shown if they're in synced groups OR directly assigned to templates.
      </div>

      {/* Search Bar */}
      <div
        style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '20px',
        }}
      >
        <input
          type="text"
          placeholder="Search by name, email, job title, or department..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            flex: 1,
            padding: '12px 15px',
            borderRadius: '6px',
            border: '2px solid #e5e7eb',
            fontSize: '14px',
          }}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            style={{
              padding: '10px 20px',
              background: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500',
            }}
          >
            Clear
          </button>
        )}
      </div>

      {/* Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '15px',
          marginBottom: '20px',
        }}
      >
        <div
          style={{
            background: 'white',
            padding: '15px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>
            Total Users
          </div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
            {stats.total}
          </div>
        </div>
        <div
          style={{
            background: 'white',
            padding: '15px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>
            On This Page
          </div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
            {filteredUsers.length}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            Loading users...
          </div>
        ) : filteredUsers.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            No users found. Sync groups from the <strong>Groups</strong> page to import users.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th
                  style={{
                    padding: '12px 15px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                  }}
                >
                  Name
                </th>
                <th
                  style={{
                    padding: '12px 15px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                  }}
                >
                  Email
                </th>
                <th
                  style={{
                    padding: '12px 15px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                  }}
                >
                  Job Title
                </th>
                <th
                  style={{
                    padding: '12px 15px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                  }}
                >
                  Groups
                </th>
                <th
                  style={{
                    padding: '12px 15px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                  }}
                >
                  Direct Templates
                </th>
                <th
                  style={{
                    padding: '12px 15px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                  }}
                >
                  Department
                </th>
                <th
                  style={{
                    padding: '12px 15px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                  }}
                >
                  Last Synced
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user, index) => (
                <tr
                  key={user.id}
                  style={{
                    borderBottom: '1px solid #e5e7eb',
                    background: index % 2 === 0 ? 'white' : '#f9fafb',
                  }}
                >
                  <td style={{ padding: '12px 15px', fontSize: '14px' }}>
                    <div style={{ fontWeight: '500', color: '#1f2937' }}>
                      {user.displayName}
                    </div>
                    {user.mobilePhone && (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        📱 {user.mobilePhone}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px 15px', fontSize: '14px', color: '#374151' }}>
                    {user.email}
                  </td>
                  <td style={{ padding: '12px 15px', fontSize: '14px', color: '#374151' }}>
                    {user.jobTitle || '-'}
                  </td>
                  <td style={{ padding: '12px 15px', fontSize: '12px' }}>
                    {user.groupMemberships && user.groupMemberships.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {user.groupMemberships.map((membership: any) => (
                          <span
                            key={membership.group.id}
                            style={{
                              background: '#eff6ff',
                              color: '#1e40af',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '500',
                            }}
                          >
                            {membership.group.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 15px', fontSize: '12px' }}>
                    {user.templateAssignments && user.templateAssignments.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {user.templateAssignments.map((assignment: any) => (
                          <span
                            key={assignment.template.id}
                            style={{
                              background: '#f0fdf4',
                              color: '#15803d',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '500',
                            }}
                          >
                            {assignment.template.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 15px', fontSize: '14px', color: '#374151' }}>
                    {user.department || '-'}
                  </td>
                  <td style={{ padding: '12px 15px', fontSize: '13px', color: '#6b7280' }}>
                    {user.lastSyncedAt
                      ? new Date(user.lastSyncedAt).toLocaleDateString()
                      : 'Never'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '10px',
            marginTop: '20px',
          }}
        >
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '8px 16px',
              background: page === 1 ? '#e5e7eb' : '#667eea',
              color: page === 1 ? '#9ca3af' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: page === 1 ? 'not-allowed' : 'pointer',
            }}
          >
            Previous
          </button>
          <span style={{ padding: '8px 16px', color: '#6b7280' }}>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: '8px 16px',
              background: page === totalPages ? '#e5e7eb' : '#667eea',
              color: page === totalPages ? '#9ca3af' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: page === totalPages ? 'not-allowed' : 'pointer',
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
